import { parse, getHours, getMinutes } from 'date-fns';

import { resourcePath } from '@gameday/utils/resource-path';

const isMobile = false;
const rules = {
    autoPlayWrapVideos: !isMobile,
    hideVideoUntilFinal: false,
    hidePitchFXData: false,
    hidePitchFXHover: isMobile,
    hideColorFeedUntilFinal: false,
    hideColorFeedSpringTraining: false,
    hideColorFeedNonMLB: true,
    hideVideoNonMLB: true,
    useGenericBatter: false,
    useGenericStadium: false,
    videoAdsVolume: '10',
    videoAdsMute: true,
    showVideoAds: !isMobile,
    minYearPreviewSupport: 2015,
    minYearWrapTabSupport: 2015,
    maxYearLegacyVideoSupport: 2017,
    minYearLiveGamedaySupport: 2015,
    showCondensedGameVideo: !isMobile,
    minYearAudioSupport: 2018,
    useSVGTeamLogo: true,
    hidePromotionPartners: false,
    showGameOfTheWeekPromo: true,
    enablePostseasonStats: true,
    showStoryline: true
};

const urlCache = {};
const GENERIC = 'GENERIC';
const BOTTOM = 'bottom';
const TOP = 'top';
const AWAY = 'away';
const HOME = 'home';
const RIGHTCODE = 'R';
const ALLSTARTYPE = 'A';
const customBottomTypes = {
    12: '12',
    14: '14',
    2394: '2394',
    2397: '14'
};

const TIMEFORMAT = 'yyyyMMdd_Hmmss';
const FINAL = 'Final';
const EMPTYTFS = ' ';
const DAY = 'day';
const NIGHT = 'night';

const calculateNightDayImage = (metaData, gameData, currentPlay) => {
    if (!gameData.venue) {
        return DAY;
    }
    const offset = gameData.venue.timeZone.offset;
    const timeData = currentPlay && currentPlay.about && currentPlay.about;
    let timecode = metaData.timeStamp; // 20210401_205448
    const playTimecode = timeData?.endTfs || timeData?.startTfs;

    // If the game is final, the current timestamp is not
    // trustworth - so use the last play timecode
    if (gameData.status.abstractGameState === FINAL && playTimecode && playTimecode !== EMPTYTFS) {
        timecode = playTimecode;
    }

    const utc = parse(timecode.split('.')[0], TIMEFORMAT, new Date());
    const homeTime = new Date(utc.setHours(utc.getHours() + offset));
    const localHour = getHours(homeTime);
    const localMin = getMinutes(homeTime);

    // eslint-disable-next-line eqeqeq
    if (((localHour >= 19 || localHour == 18) && localMin >= 30) || localHour <= 4) {
        return NIGHT;
    } else {
        return DAY;
    }
};

// Choose batter uniform of parent team id if allstar and MLB
// if available, otherwise just use the current batting team.
// We use generic batter images for milb
const batterTeamUniform = (batter, team, gameData) => {
    let uniformCode = GENERIC;

    if (team?.team?.sport?.id !== 1) {
        return uniformCode;
    }

    if (gameData?.game?.type && gameData.game.type === ALLSTARTYPE) {
        uniformCode = batter?.parentTeamId;
    } else {
        uniformCode = team?.team?.id;
    }

    return uniformCode ?? GENERIC;
};

const hotColdZones = 13;
const defaultHotColdZones = new Array(hotColdZones);

defaultHotColdZones.fill({
    zone: 'fake',
    color: null
}, 0);

class PitchFXDataPack {
    constructor (game, play = {}) {
        const { metaData = {}, gameData = {}, teamMap = {}, gamePk } = game;
        const matchup = play.matchup || {};

        const half = play?.about?.halfInning || TOP;
        const battingTeam = (half === TOP) ? AWAY : HOME;
        const pitchingTeam = (half === TOP) ? HOME : AWAY;
        const batter = matchup?.batter;
        const pitcher = matchup?.pitcher;
        const batterWithStats = game.populatePlayerFromTeam(batter, teamMap[battingTeam]);
        const pitcherWithStats = game.populatePlayerFromTeam(pitcher, teamMap[pitchingTeam]);

        const hotcoldzone = {
            batter: matchup?.batterHotColdZones?.length ? matchup.batterHotColdZones : defaultHotColdZones,
            pitcher: matchup?.pitcherHotColdZones?.length ? matchup.pitcherHotColdZones : defaultHotColdZones
        };

        const batterUniform = rules.useGenericBatter ? GENERIC : batterTeamUniform(batterWithStats, teamMap[battingTeam], gameData);
        this.play = play;
        this.season = game?.gameData?.game?.season;
        this.atbat = {
            num: play?.about?.atBatIndex,
            batter: batterWithStats,
            pitcher: pitcherWithStats,
            stand: play?.matchup?.batSide?.code || RIGHTCODE,
            pitches: play?.pitchIndex,
            getBatterImage: this.getBatterImage,
            uniform: batterUniform,
            homeAway: battingTeam
        };
        this.hidePitchFXData = rules.hidePitchFXData === true;
        this.hidePitchFXHover = this.hidePitchFXData || rules.hidePitchFXHover === true;
        this.hasPitchFX = gameData?.game?.gamedayType !== 'N';
        this.hotcoldzone = rules.hideHotColdZones !== true && hotcoldzone;
        this.stadium = gameData?.venue?.id || 'default';
        this.dayNight = calculateNightDayImage(metaData, gameData, play);
        this.gamePk = gamePk;
    }

    getBatterImage = ({ jersey, pants, isGeneric }) => {
        let fallbackImg = null;
        const atbat = (this.atbat || this);
        const batterStand = (atbat.stand || RIGHTCODE).toUpperCase() === 'L' ? 'left' : 'right';

        const jerseySrc = resourcePath.get('jersey', {
            jerseyCode: jersey,
            year: this.season,
            stand: batterStand
        });

        const pantsSrc = resourcePath.get('pants', {
            pantsCode: pants,
            year: this.season,
            stand: batterStand
        });

        if (isGeneric) {
            fallbackImg = resourcePath.get('batter', {
                team: atbat.uniform,
                homeOrAway: atbat.homeAway,
                stand: batterStand
            });
        }

        // if we don't get the expected data from the passed object, fallback to string.
        if (!jersey && !pants && !isGeneric) {
            return fallbackImg;
        }

        return isGeneric ? { jersey: null, pants: null, uniform: fallbackImg } : { jerseySrc, pantsSrc, uniform: fallbackImg };
    };

    getStadiumImages = (forceDefaultStadium, defaultStadium) => {
        const detail = '@2x';
        const dayNight = this.dayNight;
        let stadium = this.stadium;
        let stadiumBottom = (customBottomTypes[stadium] || 'default');
        const key = dayNight + detail;

        if (rules.useGenericStadium) {
            stadium = 'default';
            stadiumBottom = 'default';
        }

        const defaultStadiumTopConfig = {
            density: detail,
            phase: dayNight
        };

        const defaultStadiumBottomConfig = {
            id: stadiumBottom,
            density: detail
        };

        // override the default stadium
        if (defaultStadium) {
            defaultStadiumTopConfig.id = defaultStadium;
        }

        const defaultTop = urlCache[key] = urlCache[key] || resourcePath.get('stadiumTop', defaultStadiumTopConfig);
        const config = {
            defaultTop,
            top: urlCache[key + stadium] = urlCache[key + stadium] || (!forceDefaultStadium
                ? resourcePath.get('stadiumTop', {
                    density: detail,
                    phase: dayNight,
                    id: stadium
                })
                : defaultTop),
            bottom: urlCache[detail + BOTTOM + stadiumBottom] = urlCache[detail + BOTTOM + stadiumBottom] || resourcePath.get('stadiumBottom', defaultStadiumBottomConfig)
        };

        return config;
    };
};

export default PitchFXDataPack;
